/**
 * Review Presence brand lockup.
 *
 * The name is set in Blanka (self-hosted from /public/fonts) and paired with the
 * favicon mark. Two tones are available so the lockup reads correctly on both the
 * navy brand panels and the light application chrome.
 */
export const MARK_TRANSPARENT = "/rp-favicon.png"; // white "R" + blue pin, for navy surfaces
export const MARK_NAVY = "/rp-favicon-navy.png"; // navy tile, for light surfaces
export const LOGO_FULL = "/rp-logo-full.png";

/**
 * Sizes are driven by the mark, because the mark is the anchor of the lockup.
 *
 * The name stacks on two lines - REVIEW over PRESENCE - so the block of type sits flush with
 * the top and bottom of the mark, matching the official logo.
 *
 * Geometry, all measured in-browser rather than guessed:
 *   - Blanka's capitals render at 0.825x its font-size.
 *   - Two stacked lines therefore stand (LINE_HEIGHT + 0.825) x font-size tall.
 *   - The ink inside the mark PNG fills 80% of its box.
 * Solving those gives font-size = 0.43x the mark height, which lands the type block exactly as
 * tall as the ink in the mark.
 */
const TEXT_TO_MARK = 0.43;
const LINE_HEIGHT = 1.05;
const GAP_TO_MARK = 0.16; // horizontal gap, as a fraction of the mark height

const SIZES = {
  sm: 32, // application header
  md: 44, // cards and small panels
  lg: 60, // section headers
  xl: 80, // hero panels
};

/**
 * Full lockup: the square mark plus the stacked name.
 * tone="on-dark" for navy panels, tone="on-light" for white chrome.
 */
export const Wordmark = ({
  size = "md",
  tone = "on-dark",
  label = "Review Presence",
  className = "",
}) => {
  const px = SIZES[size] || SIZES.md;
  const onDark = tone === "on-dark";
  const words = String(label).trim().split(/\s+/);
  const lines = words.length === 2 ? words : [String(label).trim()];
  return (
    <span
      role="img"
      aria-label={label}
      className={`inline-flex shrink-0 select-none items-center self-start ${className}`}
      style={{ gap: `${Math.round(px * GAP_TO_MARK)}px` }}
      data-testid="brand-wordmark"
    >
      <img
        src={onDark ? MARK_TRANSPARENT : MARK_NAVY}
        alt=""
        aria-hidden="true"
        style={{ height: `${px}px`, width: `${px}px` }}
        className={`shrink-0 object-contain ${onDark ? "" : "rounded-lg"}`}
      />
      <span
        aria-hidden="true"
        className={`rp-wordmark ${onDark ? "text-white" : "text-[hsl(var(--rp-navy))]"}`}
        style={{
          fontSize: `${(px * TEXT_TO_MARK).toFixed(2)}px`,
          lineHeight: LINE_HEIGHT,
        }}
      >
        {lines.map((line, i) => (
          <span key={`${line}-${i}`} className="block">
            {line}
          </span>
        ))}
      </span>
    </span>
  );
};

/** Square mark on its own. */
export const Mark = ({ className = "h-8 w-8", tone = "on-light" }) => (
  <img
    src={tone === "on-dark" ? MARK_TRANSPARENT : MARK_NAVY}
    alt="Review Presence"
    className={`${className} shrink-0 select-none rounded-lg object-contain`}
  />
);

export const TenantAvatar = ({ tenant, className = "h-9 w-9" }) => {
  const initials = (tenant?.name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  if (tenant?.logo_url) {
    return (
      <span
        className={`${className} grid shrink-0 place-items-center overflow-hidden rounded-lg border border-[hsl(var(--border))] bg-white`}
      >
        <img
          src={tenant.logo_url}
          alt=""
          className="h-full w-full object-contain p-0.5"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </span>
    );
  }
  return (
    <span
      className={`${className} grid shrink-0 place-items-center rounded-lg bg-[hsl(var(--rp-navy))] text-xs font-semibold text-white`}
    >
      {initials}
    </span>
  );
};
